import { useState, useEffect } from 'react';
import { TextField, Button, Box, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import { useSnackbar } from 'notistack';
import { Cabinet } from '../../interfaces/api.interface';

interface SearchBarProps {
    onSearch: (path: string, codeCabinet: string) => void;
    cabinets?: Cabinet[];
    showCabinetSelect: boolean;
    defaultCabinetCode?: string;
    depthFolders: number;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, cabinets = [], showCabinetSelect, defaultCabinetCode, depthFolders }) => {
    const [path, setPath] = useState('');
    const [selectedCabinet, setSelectedCabinet] = useState(defaultCabinetCode || '');
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (defaultCabinetCode) {
            const cabinetExists = cabinets.some(cabinet => cabinet.ref === defaultCabinetCode);
            if (cabinetExists) {
                setSelectedCabinet(defaultCabinetCode);
            } else {
                setSelectedCabinet('');
            }
        }
    }, [defaultCabinetCode, cabinets]);
    
    const validatePath = (path: string): boolean => {
        const pathMatch = path.match(/^([a-zA-Z]):\\(.*)?/);
        return !!pathMatch;    
    };

    const validateDepth = (path: string): boolean => {
        const result = path.split(':\\')[1].split('\\');
        return result.length >= depthFolders;
    };

    const handleSearch = () => {
        if (!validatePath(path)) {
            enqueueSnackbar("Merci de spécifier un chemin valide", { variant: "error" });
            return;
        }
        if (!validateDepth(path)) {
            enqueueSnackbar("Merci de spécifier un chemin plus précis", { variant: "error" });
            return;
        }
        if (!selectedCabinet && defaultCabinetCode) {
            onSearch(path, defaultCabinetCode);
        } else {
            onSearch(path, selectedCabinet);
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            p={2}
            gap={2}
            sx={{ marginTop: 4 }}
        >
            <Box display="flex" alignItems="center" gap={2}>
                {showCabinetSelect && (
                    <FormControl variant="outlined" sx={{ flex: 4 }} required>
                        <InputLabel id="cabinet-select-label">Sélectionnez un cabinet</InputLabel>
                        <Select
                            labelId="cabinet-select-label"
                            value={selectedCabinet}
                            onChange={(e) => {
                                setSelectedCabinet(e.target.value as string);
                            }}
                            label="Sélectionnez un cabinet"
                        >
                            {cabinets.map((cabinet) => (
                                <MenuItem key={cabinet.ref} value={cabinet.ref}>
                                    {`${cabinet.ref} - ${cabinet.name}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <TextField
                    label="Entrez le chemin..."
                    variant="outlined"
                    value={path}
                    onChange={(e) => setPath(e.target.value)}
                    sx={{ flex: 6 }}
                    required
                />
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSearch}
                    startIcon={<ContentPasteSearchOutlinedIcon />}
                    disabled={!path || (showCabinetSelect && !selectedCabinet)}
                >
                    Rechercher
                </Button>
            </Box>
        </Box>
    );
};

export default SearchBar;
